import React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";

import FormLabel from "@ats/src/components/forms/FormLabel";
import FormInput from "@ats/src/components/forms/FormInput";
import FormRadio from "@ats/src/components/forms/FormRadio";
import Button from "@ats/src/components/shared/Button";
import PassingAnswerTag from "@ats/src/components/shared/PassingAnswerTag";
import { removeFromArray } from "@shared/lib/utils";

type Props = {
  label: string;
  options: any[];
  updateOptions: (options) => void;
  errors: {};
  kind: string;
  isKnockout: boolean;
};

function FormSelectBuilder(props: Props) {
  const { updateOptions, errors, options, kind, isKnockout } = props;
  const [activeValue, setActiveValue] = React.useState("");
  const isLockedToYesNo = kind === "yes_no";

  const handleClickAdd = (event) => {
    event.preventDefault();

    const tempOptions = options.concat({
      text: activeValue,
      isCorrect: kind === "multi_select" ? null : false,
    });
    updateOptions(tempOptions);
    setActiveValue("");
  };

  const handleInputChange = (name, value) => {
    setActiveValue(value);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      handleClickAdd(event);
    }
  };

  const removeOption = (option) => {
    const newArr = removeFromArray(options, (a) => a === option);
    window.logger("%c[FormSelectBuilder] removeOption", "color: #1976D2", { newArr });
    updateOptions(newArr);
  };

  const markOptionCorrect = (correctIndex) => {
    const newArr = options.map((option, index) => {
      return { ...option, isCorrect: index === correctIndex };
    });
    window.logger("%c[FormSelectBuilder] markOptionCorrect", "color: #1976D2", { newArr });
    updateOptions(newArr);
  };

  return (
    <>
      <FormLabel label="Answer choices" isRequired />
      {(isLockedToYesNo || isKnockout) && (
        <Styled.Description>
          {isKnockout
            ? "A knockout question has one passing answer. Please select it below."
            : "Yes/No questions always have these two answers. They can't be edited."}
        </Styled.Description>
      )}
      {options.length > 0 && (
        <Styled.Choices>
          {options.map((option, index) => {
            return (
              <Styled.Choice key={`${option}-${index}`}>
                {isKnockout ? (
                  <Styled.RadioWrapper>
                    <FormRadio
                      id={`passing-answer-${index}`}
                      name="passing-answer"
                      value={String(index)}
                      checked={!!option.isCorrect}
                      onChange={() => markOptionCorrect(index)}
                      label={option.text}
                    />
                  </Styled.RadioWrapper>
                ) : (
                  option.text
                )}{" "}
                <Styled.Controls>
                  {isKnockout && option.isCorrect && <PassingAnswerTag />}
                  {!isLockedToYesNo && (
                    <Button styleType="text" onClick={() => removeOption(option)}>
                      Remove
                    </Button>
                  )}
                </Styled.Controls>
              </Styled.Choice>
            );
          })}
        </Styled.Choices>
      )}
      {!isLockedToYesNo && (
        <Styled.Field>
          <FormInput
            name="option"
            placeholder="Answer text"
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            value={activeValue}
            errors={errors}
          />

          <Button styleType="secondary" onClick={handleClickAdd}>
            Add
          </Button>
        </Styled.Field>
      )}
    </>
  );
}

FormSelectBuilder.propTypes = {};

FormSelectBuilder.defaultProps = {};

export default FormSelectBuilder;

/* Styled Components
======================================================= */
let Styled: any;
Styled = {};

Styled.Field = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: FormSelectBuilder_Field;
    ${t.mb(6)}
    display: flex;
    justify-content: space-between;
    align-items: center;
    > div {
      ${[t.mb(0), t.pr(3)]}
      flex: 1;
    }
  `;
});

Styled.Description = styled.p((props) => {
  const t: any = props.theme;
  return css`
    label: FormSelectBuilder_Description;
    ${[t.text.xs, t.mt(-1), t.mb(2)]}
    color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};
    max-width: 32rem;
  `;
});

Styled.Choices = styled.ul((props) => {
  const t: any = props.theme;
  return css`
    label: FormSelectBuilder_Choices;
    ${[t.mb(3), t.rounded.md]}
    border: 1px solid ${t.dark ? t.color.gray[700] : t.color.gray[200]};
    background-color: ${t.dark ? t.color.gray[700] : t.color.white};
  `;
});

Styled.Choice = styled.ul((props) => {
  const t: any = props.theme;
  return css`
    label: FormSelectBuilder_Choice;
    ${[t.px(2), t.py(2)]}
    min-height: ${t.spacing[8]};
    border-bottom: 1px solid ${t.dark ? t.color.gray[600] : t.color.gray[200]};
    display: flex;
    justify-content: space-between;
    align-items: center;

    &:last-of-type {
      border-bottom: none;
    }

    button {
      ${[t.mr(1), t.ml(6)]}
      flex-shrink: 0;
    }
  `;
});

Styled.RadioWrapper = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: FormSelectBuilder_RadioWrapper;
    margin-top: -0.375rem;
    margin-bottom: -0.375rem;
    min-width: 0;
  `;
});

Styled.Controls = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: FormSelectBuilder_Controls;
    display: flex;
    align-items: center;
    flex-shrink: 0;

    /* Styled.Choice spaces its direct buttons apart; these sit together in a row. */
    button {
      ${[t.ml(2), t.mr(0)]}
    }
  `;
});
